import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { get } from 'js/utils/api_utils';

import {
  Navbar,
  LinkItem,
  MenuItem,
  MenuLinkSection,
  MenuLink,
  MenuMessageSection,
} from 'js/design_system/nav/Navbar';
import { SearchIcon, SearchLabel } from './search/SearchNavbarComponents';
import NavbarSearchContent, {
  JournalOption,
} from './search/NavbarSearchContent';

const Logo = styled.a`
  @import 'stylesheets/design_system/font';
  @include serif__small;

  /* More overriding of libraries.css */
  &,
  &:hover,
  &:active,
  &:focus {
    text-decoration: none;
    color: var(--text-primary, #070a15);
  }
`;

const SmallLogo = () => (
  <Logo href={routes.academia_journals_path({ source: 'publishing-top-nav' })}>
    A
  </Logo>
);

const LargeLogo = () => (
  <Logo href={routes.academia_journals_path({ source: 'publishing-top-nav' })}>
    Academia.edu Journals
  </Logo>
);

type PublishingNavbarProps = {
  messages_count: number | null;
  canSeeAboutMenu: boolean;
  isSearchPage?: boolean;
  initialQuery?: string;
  initialJournal?: { slug: string; name: string };
  journal_options?: JournalOption[];
  inDevStyle?: boolean;
};

/* Navbar used on the Academia.edu Journals homepage and related subpages (not specific to any journal) */
const PublishingNavbar: React.FC<PublishingNavbarProps> = ({
  messages_count,
  canSeeAboutMenu,
  isSearchPage = false,
  initialQuery = '',
  initialJournal,
  journal_options = [{ id: 'all', name: 'All journals' }],
  inDevStyle,
}) => {
  const [unreadMentionsCount, setUnreadMentionsCount] = useState(0);

  useEffect(() => {
    const url = routes.unread_count_api_v0_mentions_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setUnreadMentionsCount(count));
  }, []);

  const initialJournalOption = initialJournal
    ? { id: initialJournal.slug, name: initialJournal.name }
    : { id: 'all', name: 'All journals' };

  return (
    <Navbar
      logoSmall={<SmallLogo />}
      logoLarge={<LargeLogo />}
      messagesCount={messages_count}
      unreadMentionsCount={unreadMentionsCount}
      inDevStyle={inDevStyle}
      items={
        <>
          {isSearchPage && (
            <LinkItem
              title="Search"
              href={routes.academia_journals_search_path({
                source: 'publishing-top-nav',
              })}
            >
              <SearchLabel>
                <SearchIcon />
                <span>Search</span>
              </SearchLabel>
            </LinkItem>
          )}
          {!isSearchPage && (
            <MenuItem
              title="Search"
              reference={
                <SearchLabel>
                  <SearchIcon />
                  <span>Search</span>
                </SearchLabel>
              }
            >
              <NavbarSearchContent
                initialQuery={initialQuery}
                initialJournal={initialJournalOption}
                journalOptions={journal_options}
              />
            </MenuItem>
          )}

          <LinkItem
            href={routes.academia_journals_all_path({
              source: 'publishing-top-nav',
            })}
          >
            Journals
          </LinkItem>
          <LinkItem
            href={routes.academia_journals_articles_path({
              source: 'publishing-top-nav',
            })}
          >
            Articles
          </LinkItem>

          {canSeeAboutMenu && (
            <MenuItem title="About">
              <MenuLinkSection title="About our journals">
                <MenuLink
                  href={routes.academia_journals_about_about_us_path({
                    source: 'publishing-top-nav',
                  })}
                >
                  About us
                </MenuLink>
                <MenuLink
                  href={routes.academia_journals_about_why_publish_with_us_path(
                    { source: 'publishing-top-nav' },
                  )}
                >
                  Why publish with us
                </MenuLink>
                <MenuLink
                  href={routes.academia_journals_about_testimonials_path({
                    source: 'publishing-top-nav',
                  })}
                >
                  Testimonials
                </MenuLink>
                <MenuLink
                  href={routes.academia_journals_about_our_team_path({
                    source: 'publishing-top-nav',
                  })}
                >
                  Our team
                </MenuLink>
                <MenuLink
                  href={routes.academia_journals_about_contact_us_path({
                    source: 'publishing-top-nav',
                  })}
                >
                  Contact us
                </MenuLink>
              </MenuLinkSection>
              <MenuLinkSection title="Publishing model">
                <MenuLink
                  href={routes.academia_journals_about_editorial_process_path({
                    source: 'publishing-top-nav',
                  })}
                >
                  Editorial process
                </MenuLink>
                <MenuLink
                  href={routes.academia_journals_about_editorial_policies_path({
                    source: 'publishing-top-nav',
                  })}
                >
                  Editorial policies
                </MenuLink>
                <MenuLink
                  href={routes.academia_journals_about_open_access_policy_path({
                    source: 'publishing-top-nav',
                  })}
                >
                  Open access policy
                </MenuLink>
                <MenuLink
                  href={routes.academia_journals_about_article_processing_charges_path(
                    { source: 'publishing-top-nav' },
                  )}
                >
                  Article processing charges
                </MenuLink>
              </MenuLinkSection>
              <MenuMessageSection
                title="About Academia.edu"
                text="Academia.edu Journals is powered by Academia.edu, the world's largest research network."
                ctaText="Learn more about Academia.edu"
                ctaHref={routes.about_path({ source: 'publishing-top-nav' })}
              />
            </MenuItem>
          )}

          <LinkItem
            href={routes.about_path({ source: 'publishing-top-nav' })}
            external
          >
            About Academia.edu
          </LinkItem>
        </>
      }
    />
  );
};

export default PublishingNavbar;
